'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  KeyRound,
  Loader2,
  Mail,
  Phone,
  ShieldCheck,
  User,
  UserRound,
  X,
} from 'lucide-react';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PHONE_MAX_LEN, sanitizePhoneInput } from '@/lib/phoneInput';
import { randomCaptchaPair } from '@/lib/signupCaptcha';
import { USERNAME_MAX_LEN, USERNAME_MIN_LEN } from '@/lib/usernameRules';
import UserAuthPage from '@/components/UserPanel/AuthPage';
import SocialProofPopup from '@/components/UserPanel/SocialProofPopup';

export default function UserSignup() {
  const { t } = useTranslation('panel');
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const captcha = useMemo(() => randomCaptchaPair(), []);
  const usernameCheckSeq = useRef(0);
  const [usernameStatus, setUsernameStatus] = useState('idle');
  /** Shown once after signup — phrase is not stored server-side after this. */
  const [recoveryStep, setRecoveryStep] = useState(null);
  const [recoverySavedConfirmed, setRecoverySavedConfirmed] = useState(false);
  /** idle | checking | available | taken | invalid */
  const [form, setForm] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    referralId: '',
    referralType: 'auto',
    password: '',
    confirmPassword: '',
    captchaAnswer: '',
    captchaChecked: false,
  });

  useEffect(() => {
    if (!router.isReady) return;
    const ref = typeof router.query.ref === 'string' ? router.query.ref.trim().toLowerCase() : '';
    const sponsor =
      typeof router.query.sponsor === 'string'
        ? router.query.sponsor.trim().toLowerCase()
        : '';
    if (sponsor) {
      setForm((s) =>
        s.referralId
          ? s
          : { ...s, referralId: sponsor, referralType: 'agent' }
      );
      return;
    }
    if (!ref) return;
    setForm((s) => (s.referralId ? s : { ...s, referralId: ref, referralType: 'auto' }));
  }, [router.isReady, router.query.ref, router.query.sponsor]);

  useEffect(() => {
    const raw = form.username.trim();
    const lower = raw.toLowerCase();

    if (!raw) {
      setUsernameStatus('idle');
      return;
    }
    if (!/^[A-Za-z0-9]+$/.test(raw)) {
      setUsernameStatus('invalid');
      return;
    }
    if (raw.length < USERNAME_MIN_LEN) {
      setUsernameStatus('invalid');
      return;
    }

    const id = ++usernameCheckSeq.current;
    const ac = new AbortController();
    const debounceMs = 450;

    const tid = setTimeout(async () => {
      if (usernameCheckSeq.current !== id) return;
      setUsernameStatus('checking');
      try {
        const res = await fetch(
          `/api/user/check-username?username=${encodeURIComponent(lower)}`,
          { signal: ac.signal }
        );
        const data = await res.json().catch(() => ({}));
        if (usernameCheckSeq.current !== id) return;
        if (!res.ok) {
          setUsernameStatus('idle');
          return;
        }
        if (data.invalid) {
          setUsernameStatus('invalid');
          return;
        }
        setUsernameStatus(data.available ? 'available' : 'taken');
      } catch (err) {
        if (err?.name === 'AbortError') return;
        if (usernameCheckSeq.current !== id) return;
        setUsernameStatus('idle');
      }
    }, debounceMs);

    return () => {
      clearTimeout(tid);
      ac.abort();
    };
  }, [form.username]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(t('user.signup.error.nameRequired'));
    const uTrim = form.username.trim();
    const usernameOk = /^[A-Za-z0-9]+$/.test(uTrim);
    if (!usernameOk) return toast.error(t('user.signup.error.usernameAlphanumeric'));
    if (uTrim.length < USERNAME_MIN_LEN) {
      return toast.error(t('user.signup.error.usernameMin', { min: USERNAME_MIN_LEN }));
    }
    if (uTrim.length > USERNAME_MAX_LEN) {
      return toast.error(t('user.signup.error.usernameMax', { max: USERNAME_MAX_LEN }));
    }
    if (usernameStatus === 'taken') return toast.error(t('user.signup.error.usernameTaken'));
    if (usernameStatus === 'checking')
      return toast.error(t('user.signup.error.usernameCheckWait'));
    const referralTrim = form.referralId.trim().toLowerCase();
    if (!referralTrim) return toast.error(t('user.signup.error.referralRequired'));
    if (!/^[a-z0-9]+$/.test(referralTrim.replace(/-/g, ''))) {
      return toast.error(t('user.signup.error.referralInvalid'));
    }
    if (!form.email) return toast.error(t('user.signup.error.emailRequired'));
    if (!form.password) return toast.error(t('user.signup.error.passwordRequired'));
    if (!form.confirmPassword) return toast.error(t('user.signup.error.confirmRequired'));
    if (form.password !== form.confirmPassword)
      return toast.error(t('user.signup.error.passwordsMismatch'));
    if (form.password.length < 8) return toast.error(t('user.signup.error.passwordMin'));
    const captchaAns = parseInt(form.captchaAnswer.trim(), 10);
    const expected = captcha.a + captcha.b;
    if (!form.captchaChecked || !Number.isInteger(captchaAns) || captchaAns !== expected) {
      return toast.error(t('user.signup.error.captchaWrong'));
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/user/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          username: form.username.trim().toLowerCase(),
          email: form.email.trim(),
          phone: sanitizePhoneInput(form.phone).slice(0, PHONE_MAX_LEN),
          referralId: form.referralId.trim().toLowerCase(),
          referralType: form.referralType,
          sponsorAgentId:
            form.referralType === 'agent' ? form.referralId.trim().toLowerCase() : '',
          password: form.password,
          confirmPassword: form.confirmPassword,
          captchaChecked: form.captchaChecked,
          captchaA: captcha.a,
          captchaB: captcha.b,
          captchaAnswer: form.captchaAnswer.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(
          typeof data.error === 'string' ? data.error : t('user.signup.error.registrationFailed')
        );
        return;
      }
      if (typeof data.recoveryPhrase === 'string' && data.recoveryPhrase.trim()) {
        setRecoverySavedConfirmed(false);
        setRecoveryStep(data.recoveryPhrase.trim());
        toast.success(t('user.signup.toastSaveRecoveryFirst'));
        return;
      }
      toast.success(t('user.signup.toastCreated'));
      router.push('/user');
    } finally {
      setSubmitting(false);
    }
  };

  const recoveryWords = recoveryStep ? recoveryStep.split(/\s+/) : [];

  return (
    <UserAuthPage
      title={t('user.signup.title')}
      subtitle={t('user.signup.subtitle')}
      secondaryText={t('user.signup.secondaryText')}
      secondaryHref="/user"
      secondaryLabel={t('user.signup.secondaryLabel')}
    >
      <SocialProofPopup />
      {recoveryStep ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="recovery-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border bg-bg-card p-5 shadow-xl"
          >
            <h2 id="recovery-title" className="font-display text-lg font-semibold text-text-primary">
              {t('user.signup.recovery.title')}
            </h2>
            <p className="mt-2 text-sm text-text-muted">{t('user.signup.recovery.body')}</p>
            <div className="mt-3 rounded-lg border border-red-500/25 bg-red-500/10 p-3">
              <div className="flex gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-accent-red" />
                <p className="text-xs text-text-primary">{t('user.signup.recovery.warn')}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {recoveryWords.map((word, i) => (
                <div
                  key={`${i}-${word}`}
                  className="flex items-center gap-2 rounded-lg border border-border bg-bg-secondary px-2 py-2"
                >
                  <span className="w-5 shrink-0 text-xs font-semibold text-accent-amber">{i + 1}</span>
                  <span className="truncate text-sm text-text-primary">{word}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(recoveryStep);
                toast.success(t('user.signup.recovery.copiedToast'));
              }}
              className="mt-4 w-full rounded-lg border border-border bg-bg-secondary py-2.5 text-sm font-medium text-text-primary hover:bg-white/5"
            >
              {t('user.signup.recovery.copy')}
            </button>
            <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={recoverySavedConfirmed}
                onChange={(e) => setRecoverySavedConfirmed(e.target.checked)}
                className="mt-1"
              />
              <span>{t('user.signup.recovery.checkbox')}</span>
            </label>
            <button
              type="button"
              disabled={!recoverySavedConfirmed}
              onClick={() => {
                setRecoveryStep(null);
                router.push('/user');
              }}
              className="mt-4 w-full rounded-lg bg-linear-to-br from-accent-amber to-accent-orange py-2.5 font-display text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('user.signup.recovery.continue')}
            </button>
          </div>
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t('user.signup.form.labelFullName')}
          </label>
          <div className="relative">
            <UserRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              placeholder={t('user.signup.form.placeholderFullName')}
              autoComplete="name"
              className="w-full rounded-lg border border-border bg-bg-secondary py-2.5 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t('user.signup.form.labelUsername')}
          </label>
          <div className="relative">
            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={form.username}
              minLength={USERNAME_MIN_LEN}
              maxLength={USERNAME_MAX_LEN}
              onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))}
              placeholder={t('user.signup.form.placeholderUsername')}
              autoComplete="username"
              className={`w-full rounded-lg border bg-bg-secondary py-2.5 pl-10 pr-10 text-sm text-text-primary placeholder:text-text-muted focus:outline-none ${
                usernameStatus === 'available'
                  ? 'border-green-500/50 focus:border-green-500'
                  : usernameStatus === 'taken' || usernameStatus === 'invalid'
                    ? 'border-red-500/50 focus:border-red-500'
                    : 'border-border focus:border-accent-amber'
              }`}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-text-muted">
              {usernameStatus === 'checking' ? (
                <Loader2 className="h-4 w-4 animate-spin text-accent-amber" aria-hidden />
              ) : usernameStatus === 'available' ? (
                <Check className="h-4 w-4 text-green-500" aria-hidden />
              ) : usernameStatus === 'taken' || usernameStatus === 'invalid' ? (
                <X className="h-4 w-4 text-red-400" aria-hidden />
              ) : null}
            </span>
          </div>
          <p className="mt-1 text-xs text-text-muted">
            {t('user.signup.form.usernameHint', { min: USERNAME_MIN_LEN, max: USERNAME_MAX_LEN })}
          </p>
          {usernameStatus === 'available' ? (
            <p className="mt-1 text-xs text-green-500">{t('user.signup.username.available')}</p>
          ) : null}
          {usernameStatus === 'taken' ? (
            <p className="mt-1 text-xs text-red-400">{t('user.signup.username.taken')}</p>
          ) : null}
          {usernameStatus === 'invalid' && form.username.trim() ? (
            <p className="mt-1 text-xs text-red-400">
              {!/^[A-Za-z0-9]+$/.test(form.username.trim())
                ? t('user.signup.username.invalidChars')
                : form.username.trim().length < USERNAME_MIN_LEN
                  ? t('user.signup.username.invalidMin', { min: USERNAME_MIN_LEN })
                  : t('user.signup.username.invalidGeneric')}
            </p>
          ) : null}
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t('user.signup.form.labelEmail')}
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              placeholder={t('user.signup.form.placeholderEmail')}
              autoComplete="email"
              className="w-full rounded-lg border border-border bg-bg-secondary py-2.5 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t('user.signup.form.labelPhone')}
          </label>
          <div className="relative">
            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  phone: sanitizePhoneInput(e.target.value).slice(0, PHONE_MAX_LEN),
                }))
              }
              placeholder={t('user.signup.form.placeholderPhone')}
              className="w-full rounded-lg border border-border bg-bg-secondary py-2.5 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t('user.signup.form.labelReferral')}
          </label>
          <div className="relative">
            <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={form.referralId}
              onChange={(e) => setForm((s) => ({ ...s, referralId: e.target.value }))}
              placeholder={t('user.signup.form.placeholderReferral')}
              autoComplete="off"
              required
              className="w-full rounded-lg border border-border bg-bg-secondary py-2.5 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
            />
          </div>
          <p className="mt-1 text-xs text-text-muted">{t('user.signup.form.referralHint')}</p>
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t('user.signup.form.labelPassword')}
          </label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
            placeholder={t('user.signup.form.placeholderPassword')}
            autoComplete="new-password"
            className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t('user.signup.form.labelConfirmPassword')}
          </label>
          <input
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setForm((s) => ({ ...s, confirmPassword: e.target.value }))}
            placeholder={t('user.signup.form.placeholderConfirmPassword')}
            autoComplete="new-password"
            className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
          />
        </div>
        <div className="space-y-2 rounded-lg border border-border bg-bg-secondary p-3">
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={form.captchaChecked}
              onChange={(e) => setForm((s) => ({ ...s, captchaChecked: e.target.checked }))}
            />
            <ShieldCheck size={16} className="text-accent-amber" />
            {t('user.signup.form.captchaRobot')}
          </label>
          <label className="block text-xs text-text-muted">
            {t('user.signup.form.captchaQuestion', { a: captcha.a, b: captcha.b })}
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={form.captchaAnswer}
            onChange={(e) =>
              setForm((s) => ({ ...s, captchaAnswer: e.target.value.replace(/\D/g, '') }))
            }
            placeholder={t('user.signup.form.placeholderCaptcha')}
            className="w-full rounded-lg border border-border bg-bg-primary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={
            submitting ||
            usernameStatus === 'taken' ||
            usernameStatus === 'invalid' ||
            (form.username.trim() !== '' &&
              /^[A-Za-z0-9]+$/.test(form.username.trim()) &&
              form.username.trim().length >= USERNAME_MIN_LEN &&
              usernameStatus === 'checking')
          }
          className="w-full rounded-lg bg-linear-to-br from-accent-amber to-accent-orange py-2.5 font-display text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? t('user.signup.form.submitting') : t('user.signup.form.submit')}
        </button>
      </form>
    </UserAuthPage>
  );
}
